/**
 * FeatureInspector — Animated slide-up panel showing selected feature details.
 * Supports editing name/properties, linking to entities/assets, and delete.
 */

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Edit3, Save, Trash2, Link2, MapPin, Pentagon,
  Route, CircleDot, Sparkles, Check, ChevronDown,
} from 'lucide-react';
import { useMapStore } from '../MapContext';
import { useUpdateFeature, useDeleteFeature } from '../api';
import { FEATURE_STATUS_COLORS } from '../types';
import type { MapFeature } from '../types';

/* ------------------------------------------------------------------ */
/*  Animated number                                                     */
/* ------------------------------------------------------------------ */

function AnimatedNumber({ value, suffix = '', decimals = 1 }: { value: number; suffix?: string; decimals?: number }) {
  // Simple formatted display (avoids MotionValue-as-ReactNode issue)
  const formatted = value.toFixed(decimals);
  return (
    <motion.span
      key={formatted}
      initial={{ opacity: 0.5, y: 2 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {formatted}{suffix}
    </motion.span>
  );
}

/* ------------------------------------------------------------------ */
/*  Feature type icon                                                   */
/* ------------------------------------------------------------------ */

const FEATURE_ICONS: Record<string, typeof MapPin> = {
  Point: MapPin,
  Polygon: Pentagon,
  MultiPolygon: Pentagon,
  LineString: Route,
  MultiLineString: Route,
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                      */
/* ------------------------------------------------------------------ */

export default function FeatureInspector() {
  const { selectedFeature, setSelectedFeature, inspectorOpen, setInspectorOpen } = useMapStore();
  const updateFeature = useUpdateFeature();
  const deleteFeature = useDeleteFeature();

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');

  const feature = selectedFeature;
  if (!feature) return null;

  const Icon = FEATURE_ICONS[feature.feature_type] || CircleDot;
  const statusColor = FEATURE_STATUS_COLORS[feature.status] || FEATURE_STATUS_COLORS.active;
  const isAgent = feature.source === 'agent';
  const areaHa = feature.area_m2 / 10000;
  const perimeterKm = feature.perimeter_m / 1000;

  const handleSaveName = () => {
    if (editName.trim() && editName.trim() !== feature.name) {
      updateFeature.mutate({ id: feature.id, name: editName.trim() });
    }
    setEditing(false);
  };

  const handleDelete = () => {
    if (confirm(`Delete "${feature.name}"?`)) {
      deleteFeature.mutate(feature.id);
      setSelectedFeature(null);
    }
  };

  const handleConfirmReview = () => {
    updateFeature.mutate({ id: feature.id, status: 'active' });
  };

  return (
    <AnimatePresence>
      {inspectorOpen && (
        <motion.div
          initial={{ y: 200, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 200, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          className="border-t border-gray-200 bg-white/95 backdrop-blur-md"
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Icon className="w-4 h-4 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              {editing ? (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                    className="text-sm font-medium text-gray-900 bg-transparent border-b border-blue-400 outline-none w-full"
                  />
                  <button onClick={handleSaveName} className="p-1 text-blue-600 hover:text-blue-700">
                    <Save className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-gray-900 truncate">{feature.name}</span>
                  <button
                    onClick={() => { setEditName(feature.name); setEditing(true); }}
                    className="p-0.5 text-gray-300 hover:text-gray-500 transition-colors"
                  >
                    <Edit3 className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusColor.bg} ${statusColor.text}`}>
                  {feature.status}
                </span>
                {isAgent && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 flex items-center gap-0.5">
                    <Sparkles className="w-2.5 h-2.5" /> AI
                  </span>
                )}
                <span className="text-[10px] text-gray-400">{feature.feature_type}</span>
              </div>
            </div>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => { setSelectedFeature(null); setInspectorOpen(false); }}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 transition-colors"
            >
              <X className="w-4 h-4" />
            </motion.button>
          </div>

          {/* Measurements */}
          <div className="px-4 py-3 grid grid-cols-2 gap-3">
            {feature.area_m2 > 0 && (
              <div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Area</div>
                <div className="text-sm font-semibold text-gray-800">
                  <AnimatedNumber value={areaHa} suffix=" ha" />
                </div>
                <div className="text-[10px] text-gray-400">
                  <AnimatedNumber value={feature.area_m2} suffix=" m²" decimals={0} />
                </div>
              </div>
            )}
            {feature.perimeter_m > 0 && (
              <div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Perimeter</div>
                <div className="text-sm font-semibold text-gray-800">
                  <AnimatedNumber value={perimeterKm} suffix=" km" decimals={2} />
                </div>
                <div className="text-[10px] text-gray-400">
                  <AnimatedNumber value={feature.perimeter_m} suffix=" m" decimals={0} />
                </div>
              </div>
            )}
          </div>

          {/* Properties */}
          {Object.keys(feature.properties).filter(k => !k.startsWith('_')).length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100">
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">Properties</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {Object.entries(feature.properties)
                  .filter(([k]) => !k.startsWith('_'))
                  .map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2 text-xs">
                      <span className="text-gray-400 font-mono truncate w-24">{key}</span>
                      <span className="text-gray-700 truncate">{String(val)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="px-4 py-3 flex items-center gap-2 border-t border-gray-100">
            {feature.status === 'review' && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleConfirmReview}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 transition-colors"
              >
                <Check className="w-3.5 h-3.5" /> Confirm
              </motion.button>
            )}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleDelete}
              className="flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
